import {Component, computed, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TextFieldModule} from '@angular/cdk/text-field';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {SharedModule} from '../../../../../core/modules/shared.module';
import {EventsService} from '../../../../../core/services/events.service';
import {FuseConfigService} from '../../../../../core/services/config.service';

interface TaskLocation {
  id: number;
  name: string;
}

export interface TaskGroupDialogData {
  title?: string;
  description?: string;
  deadline?: string | null;
  countryId?: number;
  cityId?: number;
  organizationId?: number;
  type: 'group' | 'task';
  action: 'new' | 'edit';
}

@Component({
  selector: 'edit-task-group-modal',
  templateUrl: './edit-task-group.modal.html',
  styleUrls: ['./edit-task-group.modal.scss'],
  imports: [SharedModule, TextFieldModule],
  providers: [EventsService]
})
export class EditTaskGroupModal implements OnInit {
  private eventService = inject(EventsService);
  private fuseConfig = inject(FuseConfigService);
  private destroyRef = inject(DestroyRef);
  public dialogRef = inject(MatDialogRef<EditTaskGroupModal>);
  public data = inject<TaskGroupDialogData>(MAT_DIALOG_DATA);
  title = signal(this.data.title || '');
  description = signal(this.data.description || '');
  deadline = signal<Date | null>(this.data.deadline ? new Date(this.data.deadline) : null);
  dialogTitle = computed(() => 'taskGroup.' + this.data.action + '_' + this.data.type);
  role = this.fuseConfig.role_Sig;
  countries = signal<TaskLocation[]>([]);
  cities = signal<TaskLocation[]>([]);
  organizations = signal<TaskLocation[]>([]);
  error = signal('');
  location = {
    countryId: this.data.countryId || null,
    cityId: this.data.cityId || null,
    organizationId: this.data.organizationId || null
  };

  ngOnInit() {
    if (this.data.type !== 'group') {
      return;
    }
    this.eventService.getCountry().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (countries: TaskLocation[]) => {
        this.countries.set(countries);
        this.location.countryId = countries.find(country => country.id === this.location.countryId)?.id
          || (countries.length === 1 ? countries[0].id : null);
        if (this.location.countryId) {
          this.getCities();
        }
      },
      error: () => this.error.set('tasks.load_error')
    });
  }

  countryChanged() {
    this.location.cityId = null;
    this.location.organizationId = null;
    this.cities.set([]);
    this.organizations.set([]);
    this.getCities();
  }

  cityChanged() {
    this.location.organizationId = null;
    this.organizations.set([]);
    this.getOrganizations();
  }

  private getCities() {
    const countryId = this.location.countryId;
    this.eventService.getCity(countryId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (cities: TaskLocation[]) => {
        if (countryId !== this.location.countryId) {
          return;
        }
        this.cities.set(cities);
        this.location.cityId = cities.find(city => city.id === this.location.cityId)?.id
          || (cities.length === 1 ? cities[0].id : null);
        if (this.location.cityId) {
          this.getOrganizations();
        }
      },
      error: () => this.error.set('tasks.load_error')
    });
  }

  private getOrganizations() {
    const cityId = this.location.cityId;
    this.eventService.getOrganizationByCity(cityId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (organizations: TaskLocation[]) => {
        if (cityId !== this.location.cityId) {
          return;
        }
        this.organizations.set(organizations);
        const organizationId = this.location.organizationId || Number(this.fuseConfig.organizationId_Sig());
        this.location.organizationId = organizations.find(organization => organization.id === organizationId)?.id
          || (organizations.length === 1 ? organizations[0].id : null);
      },
      error: () => this.error.set('tasks.load_error')
    });
  }

  save() {
    const title = this.title().trim();
    if (title && (this.data.type === 'task' || (this.location.countryId && this.location.cityId && this.location.organizationId))) {
      this.dialogRef.close(this.data.type === 'task'
        ? {title, description: this.description(), deadline: this.deadline()?.toISOString() || null}
        : {title, ...this.location});
    }
  }
}
